/**
 * Settings > Organic & Offline > Online Visibility > SEO / AEO: the connections search work reads (Search Console,
 * Business Profile, per location) and those coming next (WordPress, Webflow, Google Analytics 4).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, MapPin, Search, Sparkles, TrendingUp } from 'lucide-react';
import { integrationsApi, type GbpStatus, type GscStatus } from '../api/integrationsApi';
import { integrationKeys } from '../queryKeys';
import { IntegrationCard } from './IntegrationCard';
import { buildMetadata, mapStatus } from '../utils/integrationStatus';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/Toast';

/** Connections not built yet, shown so the practice knows they are coming. */
export const SEO_COMING_SOON = [
  {
    key: 'WORDPRESS',
    displayName: 'WordPress',
    description: 'Allow AI agents to make SEO changes directly to your website.',
    icon: Globe,
  },
  {
    key: 'WEBFLOW',
    displayName: 'Webflow',
    description: 'Allow AI agents to make SEO changes on Webflow sites.',
    icon: Sparkles,
  },
  {
    key: 'GOOGLE_ANALYTICS',
    displayName: 'Google Analytics 4',
    description: 'Import traffic and conversion data.',
    icon: TrendingUp,
  },
] as const;

export function GSCCard({ locationId, canSync }: { locationId: string; canSync: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data, isLoading } = useQuery({
    queryKey: integrationKeys.gscStatus(locationId),
    queryFn: () => integrationsApi.gsc.getStatus(locationId).then((r) => r.data),
    enabled: !!locationId,
  });

  const connectMutation = useMutation({
    mutationFn: () => integrationsApi.gsc.initiateConnect().then((r) => r.data),
    onSuccess: ({ authUrl }) => {
      window.location.href = authUrl;
    },
    onError: () => toast.error('Failed to start Google Search Console connection'),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => integrationsApi.gsc.disconnect(locationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.gscStatus(locationId) });
      toast.success('Google Search Console disconnected');
    },
    onError: () => toast.error('Failed to disconnect'),
  });

  const syncMutation = useMutation({
    mutationFn: () => integrationsApi.gsc.triggerSync(locationId),
    onSuccess: () => toast.success('Sync queued — data will update shortly'),
    onError: () => toast.error('Failed to trigger sync'),
  });

  if (isLoading) return <SkeletonCard />;

  const status = mapStatus(data);
  const metadata = buildMetadata(data, (d: GscStatus) =>
    d.propertyUrl ? [{ label: 'Property', value: d.propertyUrl }] : [],
  );

  return (
    <IntegrationCard
      displayName="Google Search Console"
      description="Connect to view actual search rankings, impressions, and click data for your website."
      icon={Search}
      status={status}
      metadata={metadata}
      errorMessage={data?.lastSyncError ?? undefined}
      onConnect={() => connectMutation.mutate()}
      onDisconnect={canSync ? () => disconnectMutation.mutate() : undefined}
      onSync={canSync ? () => syncMutation.mutate() : undefined}
      connectLoading={connectMutation.isPending}
      disconnectLoading={disconnectMutation.isPending}
      syncLoading={syncMutation.isPending}
    />
  );
}

export function GBPCard({ locationId, canSync }: { locationId: string; canSync: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data, isLoading } = useQuery({
    queryKey: integrationKeys.gbpStatus(locationId),
    queryFn: () => integrationsApi.gbp.getStatus(locationId).then((r) => r.data),
    enabled: !!locationId,
  });

  const connectMutation = useMutation({
    mutationFn: () => integrationsApi.gbp.initiateConnect().then((r) => r.data),
    onSuccess: ({ authUrl }) => {
      window.location.href = authUrl;
    },
    onError: () => toast.error('Failed to start Google Business Profile connection'),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => integrationsApi.gbp.disconnect(locationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.gbpStatus(locationId) });
      toast.success('Google Business Profile disconnected');
    },
    onError: () => toast.error('Failed to disconnect'),
  });

  const syncMutation = useMutation({
    mutationFn: () => integrationsApi.gbp.triggerSync(locationId),
    onSuccess: () => toast.success('Sync queued — data will update shortly'),
    onError: () => toast.error('Failed to trigger sync'),
  });

  if (isLoading) return <SkeletonCard />;

  const status = mapStatus(data);
  const metadata = buildMetadata(data, (d: GbpStatus) =>
    d.displayName ? [{ label: 'Listing', value: d.displayName }] : [],
  );

  return (
    <IntegrationCard
      displayName="Google Business Profile"
      description="Sync your GBP listing to track reviews, local rank, and citation health."
      icon={MapPin}
      status={status}
      metadata={metadata}
      errorMessage={data?.lastSyncError ?? undefined}
      onConnect={() => connectMutation.mutate()}
      onDisconnect={canSync ? () => disconnectMutation.mutate() : undefined}
      onSync={canSync ? () => syncMutation.mutate() : undefined}
      connectLoading={connectMutation.isPending}
      disconnectLoading={disconnectMutation.isPending}
      syncLoading={syncMutation.isPending}
    />
  );
}
